/**
 * The resumable installer upload the Upload Installer dialog runs: a tus 1.0.0 client against
 * the portal's own upload service, sending the file in fixed chunks, retrying a failed chunk
 * itself, and resuming from the offset the service reports.
 *
 * The chunk size is the service's hash window; the service refuses any other length, so it is
 * read from the service's OPTIONS answer and never guessed here.
 */
import * as tus from 'tus-js-client'

export const UPLOAD_ENDPOINT = '/api/tenant/uploads/'

/** Delays before each automatic retry of a failed chunk; the dialog shows an error only after all of them. */
export const RETRY_DELAYS_MS = [0, 2000, 5000, 10000, 30000, 60000]

export interface UploadLimits {
  /** Tus-Max-Size: the largest installer the service accepts, in bytes */
  maxBytes: number
  /** Tus-Chunk-Size: the length of every chunk but the last, in bytes */
  chunkBytes: number
  /** Upload-File-Types: the file types this environment accepts, such as .msi */
  fileTypes: string[]
}

/** The transport the client sends requests through. Tests set `httpStack` to an in-memory tus server. */
export const transport: { httpStack?: tus.HttpStack } = {}

export interface InstallerUploadCallbacks {
  onProgress: (bytesSent: number, bytesTotal: number) => void
  onSuccess: (uploadId: string) => void
  onError: (error: Error | tus.DetailedError) => void
}

function authorizationHeader(): Record<string, string> {
  try {
    const stored = localStorage.getItem('auth-storage')
    const token = stored ? JSON.parse(stored)?.state?.token : null
    return token ? { Authorization: `Bearer ${token}` } : {}
  } catch {
    return {}
  }
}

/** The upload id from the URL the service gave the upload: its last path segment. */
export function uploadIdFromUrl(url: string | null): string | null {
  if (!url) return null
  const segments = url.split('?')[0].split('/').filter(Boolean)
  return segments.length ? segments[segments.length - 1] : null
}

/** The HTTP status behind a tus error, or 0 for a network failure. */
export function uploadErrorStatus(error: unknown): number {
  const response = (error as tus.DetailedError)?.originalResponse
  return response && typeof response.getStatus === 'function' ? response.getStatus() : 0
}

/** The JSON `detail` the service sent with a refusal, if any. */
export function uploadErrorDetail(error: unknown): string | null {
  const response = (error as tus.DetailedError)?.originalResponse
  if (!response || typeof response.getBody !== 'function') return null
  try {
    const body = response.getBody()
    const parsed = typeof body === 'string' ? JSON.parse(body) : body
    return typeof parsed?.detail === 'string' ? parsed.detail : null
  } catch {
    return null
  }
}

export function createInstallerUpload(
  file: File,
  metadata: { filename: string; filetype: string; appName: string },
  chunkBytes: number,
  callbacks: InstallerUploadCallbacks,
): tus.Upload {
  const upload = new tus.Upload(file, {
    // Absolute: the service answers a relative Location and the client resolves it against this
    endpoint: new URL(UPLOAD_ENDPOINT, window.location.origin).toString(),
    chunkSize: chunkBytes,
    retryDelays: RETRY_DELAYS_MS,
    metadata,
    headers: authorizationHeader(),
    // Resume is per dialog, not per browser: a reopened portal starts over
    storeFingerprintForResuming: false,
    // Retried by itself: a failure on the wire, a lock or offset disagreement the next request
    // resolves, and a proxy or service fault. Not a 503: that is the service saying its storage
    // is unavailable, which the user is told at once rather than after two minutes of retries.
    onShouldRetry: (error, retryAttempt) => {
      if (retryAttempt >= RETRY_DELAYS_MS.length) return false
      const status = uploadErrorStatus(error)
      return status === 0 || status === 409 || status === 423 || status === 500 || status === 502 || status === 504
    },
    ...transport,
    onUploadUrlAvailable: () => {
      // Once the upload exists, a HEAD the service refuses (expired, deleted) must surface as
      // that refusal. With an endpoint still set the client would quietly create a second
      // upload from byte 0 instead.
      upload.options.endpoint = null
    },
    onProgress: callbacks.onProgress,
    onSuccess: () => {
      const id = uploadIdFromUrl(upload.url)
      if (id) callbacks.onSuccess(id)
      else callbacks.onError(new Error('The upload finished without an upload id'))
    },
    onError: callbacks.onError,
  })
  return upload
}
